import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import { UserService } from 'src/app/shared/services/user.service';
import { MatDialog } from '@angular/material/dialog';
import { AzureAdB2C } from 'src/environments/environment';
import { ExportTableService } from 'src/app/features/service/export-table.service';
import { AssignToLabtechComponent } from 'src/app/shared/components/assign-to-labtech/assign-to-labtech.component';
import { SetStatusComponent } from 'src/app/shared/components/set-status/set-status.component';
import { WorksheetResponse } from 'src/app/features/worksheet/models/worksheet-response.modal';
import { FormService } from 'src/app/shared/services/form.service';
import { IDropdown } from 'src/app/shared/models/i-dropdown-type.modal';
import { PrecastService } from 'src/app/shared/services/precast.service';
import { DateTimeUtils } from 'src/app/shared/util/date-time.utils';
import { ReportService } from '../../../services/precast-report.service';

// Default imports are the real pdfmake objects; a namespace import (import * as) is read-only in optimized builds
(pdfMake as any).vfs = pdfFonts.pdfMake.vfs;

@Component({
    selector: 'app-view-precast-report',
    templateUrl: './view-precast-report.component.html',
    styleUrls: ['./view-precast-report.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ViewPrecastReportComponent implements OnInit {
  projectId = '';
  soilType = '';
  reportOwner: any;
  selectedIndex = 0;
  userRoles: string[];
  statusId: any;
  statusLabel: any;
  firstReviewerName:any;
  secondReviewerName :any;
  projectReference : any;
  preapproval: any;
  daysLeft = 0;

  public worksheetRecords!: WorksheetResponse;

  constructor(
    public activatedRoute: ActivatedRoute,
    public userService: UserService,
    public dialog: MatDialog,
    private exporttableService: ExportTableService,
    private formService: FormService,
    private precastService: PrecastService,
    private reportService: ReportService,
  ) {}

  ngOnInit(): void {
    this.userAccountAndRole();
    this.soilType = 'precast';
    this.projectId = this.activatedRoute.snapshot.paramMap.get('id') ?? '';
    this.getProjectInfo(this.projectId, this.soilType);
    this.reviewAssignSet();
    this.fetchAllStatus();
  }

  userAccountAndRole(): void {
    this.userService.userRole?.subscribe({
      next: (roles) => {
        this.userRoles = roles || [];
      },
    });
  }

  roleCheck(roles: string[]): boolean {
    return roles.some((role) => this.userRoles.includes(role));
  }

  refresh(): void {
    window.location.reload();
  }

  openWorksheet(): void {
    const url = `${AzureAdB2C.redirectUri}precast-worksheet?projectId=${this.projectId}`;
    window.open(url, '_blank');
  }

  openViewSubmissionForm(): void {
    const url = `${AzureAdB2C.redirectUri}review/form/${this.soilType}/${this.projectId}`;
    window.open(url, '_blank');
  }

  printCover() {
    const html = this.exporttableService.buildCoverPreviewHtmlPrecast(this.worksheetRecords, this.soilType);
    const newWindow = window.open('', '_blank');
    if (!newWindow) {
      return;
    }

    newWindow.document.open();
    newWindow.document.write(html);
    newWindow.document.close();
  }

  openAssignToDialog() {
    this.dialog
      .open(AssignToLabtechComponent, {
        data: {
          projectId: this.projectId,
          type: this.soilType,
          role: this.userRoles,
        },
        disableClose: true,
        width: '40%',
      })
      .afterClosed()
      .subscribe((userInfo) => {
        if (userInfo) {
          this.reportOwner = `${userInfo.firstName} ${userInfo.lastName}`;
        }
      });
  }

  openSetStatusDialog() {
    this.dialog
      .open(SetStatusComponent, {
        data: {
          projectId: this.projectId,
        },
        disableClose: true,
        width: '40%',
      })
      .afterClosed()
      .subscribe((status: any) => {
        if (status.statusId) {
          this.statusId = status?.statusId;
          this.fetchAllStatus();
          this.reviewAssignSet();
          this.refreshPage();
        }
      });
  }

  getProjectInfo(projectId: string, soilType: string): void {
    this.formService?.fetchFormData(projectId, soilType)?.subscribe({
      next: (data) => {
        this.worksheetRecords = data;
        this.preapproval = data.project.preApproval;
        this.projectReference = data.project.referenceId;
        this.setDaysLeft();
      },
      error: () => {},
    });
  }

  private setDaysLeft(): void {
    const dueDate = this.worksheetRecords?.project?.dueDate;
    if (!dueDate) {
      this.daysLeft = 0;
      return;
    }
    const due = new Date(DateTimeUtils.getLocalDateTimeFromUTCDate(dueDate));
    this.daysLeft = this.findDateDifference(new Date(), due);
  }

  findDateDifference(startDate: Date, endDate: Date): number {
    const timeDifference = Math.abs(endDate.getTime() - startDate.getTime());
    const dayDifference = timeDifference / (1000 * 3600 * 24);
    return Math.trunc(dayDifference);
  }

  private fetchAllStatus(): void {
    this.precastService?.fetchProjectsLookup()?.subscribe({
      next: (lookupResponse: IDropdown[]) => {
        this.statusLabel = lookupResponse.find((status) => (status.id === this.statusId ? status : ''))?.label;
      },
    });
  }

  reviewAssignSet(): void {
    this.reportService.getReviewAssign(this.projectId)?.subscribe({
      next: (data) => {
        this.firstReviewerName = data && data.firstReviewer
          ? `${data.firstReviewer.firstName} ${data.firstReviewer.lastName}`
          : '';
        this.secondReviewerName = data && data.secondReviewer
          ? `${data.secondReviewer.firstName} ${data.secondReviewer.lastName}`
          : '';
      },
      error: () => {},
    });
  }

  refreshPage(): void {
    this.formService?.updateData(null);
    this.reviewAssignSet();
  }
}
